import { takeLatest, select, put, call, all, take, fork } from 'redux-saga/effects';
import type { AppState } from '~/reducers';
import { fetchUserInfoFromFleet } from '~/sagas/authentication-sagas';
import { populateAuthentication, setAuthenticated } from '~/ducks/authentication/actions';
import { getLocaleFromPath } from '~/utils/path-helper/path-helper';
import { redirectUser } from '~/ducks/router/actions';
import {
  getValuesFromQueryString,
  removeFromSearchString,
} from '~/utils/query-helper/query-helper';
import { initializeApp, setInitializingApp } from '~/components/containers/AppContainer/reducer';
import LocalStorageUtils from '~/utils/local-storage-utils/local-storage-utils';
import { initDatepickerLocale } from '~/config/react-datepicker';
import oidcAuthApi from '~/domain/auth/api/oidc-auth-api';
import { LogoutStatus } from '~/domain/auth/models/LogoutStatus';

/**
 * Gets token and provider from either GET-query or LocalStorage.
 * Token and provider might be undefined
 * (Exported for testing purposes)
 * @returns {{token: string|undefined, provider: string|undefined}}
 */
export function* getTokenAndProvider() {
  const getQueryAuth = yield call(getValuesFromQueryString, globalThis.location.search, [
    'token',
    'provider',
    'expiresAt',
    'refreshToken',
  ]);
  const localStorageAuth = yield call(LocalStorageUtils.getAuth);

  let token, provider, expiresAt, refreshToken;
  if (getQueryAuth.token && getQueryAuth.provider) {
    token = getQueryAuth.token;
    provider = getQueryAuth.provider;
    expiresAt = getQueryAuth.expiresAt;
    refreshToken = getQueryAuth.refreshToken;

    const newSearchString = yield call(removeFromSearchString, globalThis.location.search, [
      'token',
      'provider',
      'expiresAt',
      'refreshToken',
    ]);
    yield put(
      redirectUser({
        path: undefined,
        search: newSearchString,
        pushToHistory: false,
      })
    );
  } else if (localStorageAuth.token) {
    token = localStorageAuth.token;
    provider = localStorageAuth.provider;
    expiresAt = localStorageAuth.expiresAt;
    refreshToken = localStorageAuth.refreshToken;
  }
  return { token, provider, expiresAt, refreshToken };
}

export const getAuthenticationSelector = (state: AppState) => state.authentication;

export function* backgroundRefreshTask() {
  const { logoutStatus } = yield select(getAuthenticationSelector);
  if (logoutStatus !== LogoutStatus.Idle) {
    return;
  }

  // The user populated during init may come from the service worker cache, so re-read it
  // from the network.
  yield put(
    oidcAuthApi.endpoints.refreshCurrentUser.initiate(undefined, {
      forceRefetch: true,
    })
  );
  const resultAction = yield take([
    oidcAuthApi.endpoints.refreshCurrentUser.matchFulfilled,
    oidcAuthApi.endpoints.refreshCurrentUser.matchRejected,
  ]);
  if (oidcAuthApi.endpoints.refreshCurrentUser.matchFulfilled(resultAction)) {
    yield put(populateAuthentication(resultAction.payload));
  }
}

export function* initApp({ payload: { redirectUrl } }: ReturnType<typeof initializeApp>) {
  yield put(setInitializingApp(true));

  const pathname = globalThis.location.pathname;

  // Parallelize independent calls
  const [authBeforeUserFetched] = yield all([
    select(getAuthenticationSelector),
    call(initDatepickerLocale),
  ]);

  const guestLocation =
    ['/oidc/callback', '/uptime/callback', '/uptime/logout', '/signup'].includes(pathname) ||
    pathname.includes('/accept-user-invite/') ||
    pathname.includes('/login');

  if (
    authBeforeUserFetched.logoutStatus === LogoutStatus.Idle &&
    (authBeforeUserFetched.authenticated || !guestLocation) &&
    !authBeforeUserFetched?.isUserInfoFetched
  ) {
    yield call(fetchUserInfoFromFleet);
    const authAfterUserFetched = yield select(getAuthenticationSelector);
    if (authAfterUserFetched?.isUserInfoFetched) {
      yield put(
        setAuthenticated({
          status: true,
        })
      );
    }
  }

  yield all([select(getAuthenticationSelector), call(getLocaleFromPath, pathname)]);

  if (redirectUrl) {
    yield put(redirectUser({ path: redirectUrl, pushToHistory: false }));
  }

  yield put(setInitializingApp(false));
  yield fork(backgroundRefreshTask);
}

export default [
  function* initAppSaga() {
    yield takeLatest(initializeApp.type, initApp);
  },
];
